import React from 'react';
import { Calendar, Clock, ExternalLink, Handshake, MapPin, Ticket } from 'lucide-react';
import { formatEventDateRange, type Event } from '../utils/events';
import CountdownTimer from './CountdownTimer';

interface EventCardProps {
  event: Event;
  isUpcoming?: boolean;
}

const EventCard: React.FC<EventCardProps> = ({ event, isUpcoming = false }) => {
  const dateParts = formatEventDateRange(event).split(', ');

  return (
    <article className="surface group grid overflow-hidden sm:grid-cols-[138px_minmax(0,1fr)]">
      <div className={`flex flex-row items-center justify-between gap-4 px-5 py-4 text-white sm:flex-col sm:items-start sm:justify-start sm:px-6 sm:py-7 ${
        isUpcoming ? 'bg-[#2457a7]' : 'bg-[#38465a]'
      }`}>
        <Calendar className="h-5 w-5 text-white/70" />
        <div className="sm:mt-auto">
          <p className="text-lg font-extrabold leading-tight sm:text-xl">{dateParts[0]}</p>
          {dateParts[1] && <p className="mt-1 text-xs font-bold text-white/65">{dateParts[1]}</p>}
        </div>
      </div>

      <div className="p-5 sm:p-7">
        <div className="flex items-start justify-between gap-4">
          <h3 className="text-xl font-extrabold text-[#172033]">
            {event.url ? (
              <a href={event.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 transition hover:text-[#2457a7]">
                {event.title}
                <ExternalLink className="h-4 w-4 flex-none" />
              </a>
            ) : event.title}
          </h3>
          {isUpcoming && <span className="hidden bg-[#e8eff9] px-2.5 py-1 text-[10px] font-bold uppercase text-[#2457a7] sm:block">Upcoming</span>}
        </div>

        <div className="mt-5 space-y-3 text-sm leading-6 text-[#667085]">
          <p className="flex items-start gap-3">
            <Clock className="mt-1 h-4 w-4 flex-none text-[#d95f75]" />
            <span>{event.time}</span>
          </p>
          <p className="flex items-start gap-3">
            <MapPin className="mt-1 h-4 w-4 flex-none text-[#d95f75]" />
            <span>{event.location}</span>
          </p>
        </div>

        {isUpcoming && event.ticketUrl && (
          <div className="mt-5 border-l-4 border-[#d95f75] bg-[#fff3f5] p-4">
            <p className="mb-3 text-sm font-bold text-[#172033]">Join us at {event.title}</p>
            <a
              href={event.ticketUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Buy tickets for ${event.title} on Tixr (opens in a new tab)`}
              className="btn-primary w-full shadow-[0_4px_12px_rgba(36,87,167,0.2)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#2457a7]"
            >
              <Ticket className="h-5 w-5 flex-none" aria-hidden="true" />
              Buy Tickets
              <ExternalLink className="ml-auto h-4 w-4 flex-none" aria-hidden="true" />
            </a>
            <p className="mt-2 text-xs text-[#667085]">Purchase directly on Tixr</p>
          </div>
        )}

        {event.partner && (
          <div className="mt-5 border-t border-[#e3e7ec] pt-4">
            <p className="flex items-center gap-2 text-xs font-bold uppercase text-[#4b5565]">
              <Handshake className="h-4 w-4 text-[#d95f75]" />
              Event partner: {event.partner.name}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {event.partner.links.map((link) => (
                <a key={link.url} href={link.url} target="_blank" rel="noopener noreferrer" className="border border-[#d8dee7] bg-white px-3 py-1.5 text-xs font-bold text-[#2457a7] transition hover:border-[#2457a7] hover:bg-[#f2f6fb]">
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        )}

        {isUpcoming && <CountdownTimer targetDate={event.date} />}
      </div>
    </article>
  );
};

export default EventCard;
